import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, BedDouble, Bath, Home, MapPin, Phone, Mail, ShieldCheck, Check, LayoutTemplate,
} from "lucide-react";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import RoomCard from "@/components/RoomCard";
import PropertyMap from "@/components/PropertyMap";
import { LANDLORD } from "@/data/landlord";

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    setActiveImage(0);
    api
      .get(`/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#EFF0F2] dark:bg-[#12100E]">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-32 text-center">
          <h1 className="font-serif text-4xl text-[#1A1715] dark:text-[#F7F4F0]">Residence not found</h1>
          <Link to="/" className="mt-4 inline-block text-[#A05A32] dark:text-[#D9773F] hover:underline">
            Back to the collection
          </Link>
        </main>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-[#EFF0F2] dark:bg-[#12100E]">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-16">
          <div className="aspect-[16/9] max-w-4xl rounded-2xl bg-[#ECE5DD] dark:bg-[#231F1C] animate-pulse" />
        </main>
      </div>
    );
  }

  const gallery = property.gallery?.length ? property.gallery : [property.image];
  const rooms = property.rooms || [];
  const floorplans = [property.floorplan, ...(property.floorplans || [])].filter(Boolean);
  const specs = [
    { icon: BedDouble, label: "Bedrooms", value: property.bedrooms },
    { icon: Bath, label: "Bathrooms", value: property.bathrooms },
    { icon: Home, label: "Property Type", value: property.type },
  ];

  return (
    <div className="min-h-screen bg-[#EFF0F2] dark:bg-[#12100E]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <Link
          to="/"
          data-testid="property-detail-back-link"
          className="inline-flex items-center gap-2 text-sm text-[#57524E] dark:text-[#C7BEB5] hover:text-[#A05A32] dark:hover:text-[#D9773F] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Collection
        </Link>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-8">
            <div className="rounded-2xl overflow-hidden border border-[#E4DBD0] dark:border-[#352F2B] aspect-[16/10]">
              <img
                data-testid="property-detail-main-image"
                src={gallery[activeImage]}
                alt={property.title}
                className="h-full w-full object-cover"
              />
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-3 overflow-x-auto no-scrollbar">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    data-testid={`property-detail-thumb-${i}`}
                    onClick={() => setActiveImage(i)}
                    className={`shrink-0 w-24 aspect-[4/3] rounded-lg overflow-hidden border-2 transition-colors ${
                      i === activeImage ? "border-[#A05A32]" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={src} alt={`${property.title} ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.2em] font-mono text-[#A05A32] dark:text-[#D9773F]">{property.type}</p>
              <h1 data-testid="property-detail-title" className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-serif font-light tracking-tight text-[#1A1715] dark:text-[#F7F4F0]">
                {property.title}
              </h1>
              <p className="mt-3 flex items-center gap-2 text-[#8A837C] dark:text-[#8E867E]">
                <MapPin className="h-4 w-4" /> {property.location}
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {specs.map((s) => (
                <div key={s.label} className="rounded-xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-4">
                  <s.icon className="h-5 w-5 text-[#A05A32] dark:text-[#D9773F]" />
                  <p className="mt-2 font-serif text-xl text-[#1A1715] dark:text-[#F7F4F0]">{s.value}</p>
                  <p className="text-xs text-[#8A837C] dark:text-[#8E867E] uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#1A1715] dark:text-[#F7F4F0]">About this residence</h2>
              <p data-testid="property-detail-description" className="mt-4 text-base text-[#57524E] dark:text-[#C7BEB5] leading-relaxed">
                {property.description}
              </p>
            </div>

            <div className="mt-10">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#1A1715] dark:text-[#F7F4F0]">Location</h2>
              <p className="mt-1.5 text-sm text-[#8A837C] dark:text-[#8E867E] flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {property.location}
              </p>
              <div className="mt-4">
                <PropertyMap properties={[property]} testId="property-detail-map" height="h-72" />
              </div>
            </div>

            {floorplans.length > 0 && (
              <div className="mt-10" data-testid="floorplans-section">
                <h2 className="text-2xl sm:text-3xl font-serif text-[#1A1715] dark:text-[#F7F4F0]">Floor plans</h2>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {floorplans.map((u, i) => (
                    <a
                      key={i}
                      href={u}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`floorplan-thumb-${i}`}
                      className="rounded-xl overflow-hidden border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] hover:border-[#A05A32] transition-colors"
                    >
                      <img src={u} alt={`Floor plan ${i + 1}`} loading="lazy" className="h-40 w-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {rooms.length > 0 && (
              <div className="mt-10" data-testid="rooms-section">
                <h2 className="text-2xl sm:text-3xl font-serif text-[#1A1715] dark:text-[#F7F4F0]">Rooms in this house</h2>
                <p className="mt-1.5 text-sm text-[#8A837C] dark:text-[#8E867E]">
                  {rooms.filter((r) => r.status === "Available").length} of {rooms.length} rooms currently available
                </p>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {rooms.map((r) => (
                    <RoomCard key={r.id} room={r} propertyTitle={property.title} />
                  ))}
                </div>
              </div>
            )}

            {property.amenities?.length > 0 && (
              <div className="mt-10">
                <h2 className="text-2xl sm:text-3xl font-serif text-[#1A1715] dark:text-[#F7F4F0]">Amenities</h2>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {property.amenities.map((a) => (
                    <span
                      key={a}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] px-4 py-2 text-sm text-[#57524E] dark:text-[#C7BEB5]"
                    >
                      <Check className="h-3.5 w-3.5 text-[#2D4B3E] dark:text-[#4E826B]" /> {a}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 rounded-2xl border border-[#E4DBD0] dark:border-[#352F2B] bg-white dark:bg-[#1A1715] p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p data-testid="property-detail-price" className="font-serif text-3xl sm:text-4xl text-[#1A1715] dark:text-[#F7F4F0]">
                    {rooms.length > 0 ? `${rooms.length} Rooms` : property.type}
                  </p>
                  {rooms.length > 0 && (
                    <p className="mt-1 text-xs uppercase tracking-wider text-[#8A837C] dark:text-[#8E867E]">
                      {rooms.filter((r) => r.status === "Available").length} available · room prices below
                    </p>
                  )}
                </div>
                <StatusBadge status={property.status} testId="property-detail-status" />
              </div>
              <div className="my-6 border-t border-[#EDE6DE] dark:border-[#28231F]" />
              <p className="text-xs uppercase tracking-[0.2em] font-mono text-[#8A837C] dark:text-[#8E867E]">Contact the owner directly</p>
              <p className="mt-2 font-serif text-xl text-[#1A1715] dark:text-[#F7F4F0]">{LANDLORD.name}</p>
              <p className="text-sm text-[#8A837C] dark:text-[#8E867E]">{LANDLORD.title}</p>
              <a
                data-testid="property-detail-contact-call"
                href={LANDLORD.phoneHref}
                className="mt-5 flex items-center justify-center gap-2 rounded-full bg-[#A05A32] hover:bg-[#864522] text-[#EFF0F2] px-5 py-3 text-sm font-medium transition-colors"
              >
                <Phone className="h-4 w-4" /> {LANDLORD.phone}
              </a>
              <a
                data-testid="property-detail-contact-email"
                href={LANDLORD.emailHref}
                className="mt-3 flex items-center justify-center gap-2 rounded-full border border-[#E4DBD0] dark:border-[#352F2B] px-5 py-3 text-sm font-medium text-[#1A1715] dark:text-[#F7F4F0] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors"
              >
                <Mail className="h-4 w-4" /> Email the Owner
              </a>
              {floorplans.length > 0 && (
                <a
                  data-testid="property-detail-floorplan"
                  href={floorplans[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex items-center justify-center gap-2 rounded-full border border-[#E4DBD0] dark:border-[#352F2B] px-5 py-3 text-sm font-medium text-[#1A1715] dark:text-[#F7F4F0] hover:bg-[#F4EFEA] dark:hover:bg-[#231F1C] transition-colors"
                >
                  <LayoutTemplate className="h-4 w-4" /> View floor plan{floorplans.length > 1 ? "s" : ""}
                </a>
              )}
              <div className="mt-5 flex items-start gap-2 text-xs text-[#2D4B3E] dark:text-[#4E826B]">
                <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
                Professional service, clear communication. Message us any time — we usually reply within 2 hours.
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
